import React, { useState } from 'react';
import './Login.scss';
import { BaseRestDataProvider } from '../../../data/BaseRestDataProvider';
import API_BASE_URL from '../../../config';
import ApiUrlManager from '../../../data/ApiUrlManager';
import { EnvironmentRestDataProvider, MicrosoftEntraConfig } from '../../../data/EnvironmentRestDataProvider';
import { PublicClientApplication } from "@azure/msal-browser";
import { useTranslation } from 'react-i18next';

interface LoginProps {
  onLogin: () => void;
}



const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { t } = useTranslation('auth');
  const isElectron = navigator.userAgent.toLowerCase().includes('electron');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entraConfig, setEntraConfig] = useState<MicrosoftEntraConfig | null>(null);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const apiBase = API_BASE_URL || '';
      // base64 encode the email
      const base64 = btoa(email);
      const response = await fetch(`${apiBase}/api/environment/${encodeURIComponent(base64)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      // 404 means no Entra config (e.g. Electron/local mode) — proceed directly to password step.
      if (response.status === 404) {
        setUsername(email);
        setStep('password');
        setTimeout(() => {
          const pw = document.getElementById('login-password-field');
          if (pw) (pw as HTMLInputElement).focus();
        }, 50);
        return;
      }
      if (!response.ok) throw new Error(await response.text());
      const config = await response.json();

      setEntraConfig(config);
      if (config && config.isEnabled) {
        const loginRequest = {
          scopes: ["openid", "profile", "email", config.clientAudience]
        };

        // Microsoft login flow
        const msalInstance = new PublicClientApplication({
          auth: {
            clientId: config.clientID,
            authority: `${config.loginURL}/${config.tenantID}/v2.0`,
            redirectUri: config.redirectUri || window.location.origin,
            postLogoutRedirectUri: config.postLogoutRedirectUri || window.location.origin,
          }
        });
        await msalInstance.initialize();
        msalInstance.loginPopup({
          ...loginRequest,
          authority: `${config.loginURL}/${config.tenantID}/v2.0`,
          redirectUri: config.redirectUri || window.location.origin,
        })
        .then(response => {
          const accessToken = response.accessToken;
          BaseRestDataProvider.setToken(accessToken);
          onLogin();
        })
        .catch((error) => {
          setError(t('errors.microsoftLoginFailed'));
        });
      } else {
        // Proceed to password step
        setUsername(email);
        setStep('password');
        setTimeout(() => {
          const pw = document.getElementById('login-password-field');
          if (pw) (pw as HTMLInputElement).focus();
        }, 50);
      }
    } catch (err) {
      setError(t('errors.cannotDetermineAuthMethod'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      ApiUrlManager.setBaseUrl(API_BASE_URL || window.location.origin);
      const apiBase = API_BASE_URL || '';
      const response = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      if (data.token) {
        ApiUrlManager.setTenantID(data.tenantId);
        BaseRestDataProvider.setToken(data.token);
        onLogin();
      } else {
        setError(t('errors.invalidServerResponse'));
        setPassword('');
        setTimeout(() => {
          const pw = document.getElementById('login-password-field');
          if (pw) (pw as HTMLInputElement).focus();
        }, 50);
      }
    } catch (err: any) {
      setError(t('errors.loginFailed'));
      setPassword('');
      setTimeout(() => {
        const pw = document.getElementById('login-password-field');
        if (pw) (pw as HTMLInputElement).focus();
      }, 50);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-fullscreen-bg"
      style={{
        background: `url('${isElectron ? './img/loginbg.jpg' : '/img/loginbg.jpg'}') center center/cover no-repeat`,
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <form className="login-form" onSubmit={step === 'email' ? handleEmailContinue : handleSubmit}>
        <h1>{t('signIn')}</h1>
        {step === 'email' && (
          <>
            <div className="login-field">
              <input
                type={isElectron ? 'text' : 'email'}
                placeholder={isElectron ? t('username') : t('emailAddress')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoFocus
                required
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={loading} className="login-btn">
              {loading ? t('checking') : t('continue')}
            </button>
          </>
        )}
        {step === 'password' && (
          <>
            <div className="login-field">
              <input
                type="text"
                placeholder={t('username')}
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div className="login-field">
              <input
                id="login-password-field"
                type="password"
                placeholder={t('password')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={loading} className="login-btn">
              {loading ? t('signingIn') : t('signIn')}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default Login;
